import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AdminAccount } from "@/api/rest/models/admin";
import {
  getDevelopmentDetails,
  listAccounts,
  listDevelopments,
  updateDevelopmentName,
  updateDevelopmentOwner,
} from "@/api/rest/services/adminService";
import Spinner from "@/components/common/Spinner";

import CollapsibleSection from "./CollapsibleSection";
import LogoEditor from "./LogoEditor";

const DEFAULT_OWNER_ACCOUNT_ID = 3;

type SortColumn = "id" | "name";
type SortDirection = "asc" | "desc";

export default function DevelopmentsTab({
  selectedDevelopmentId,
  onSelectedDevelopmentIdChange,
  onSelectAccount,
  onSelectBuilding,
}: {
  selectedDevelopmentId: number | null;
  onSelectedDevelopmentIdChange: (developmentId: number | null) => void;
  onSelectAccount: (accountId: number) => void;
  onSelectBuilding: (buildingId: number) => void;
}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  const search = searchParams.get("developmentSearch") ?? "";
  const setSearch = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("developmentSearch", value);
    } else {
      newParams.delete("developmentSearch");
    }
    setSearchParams(newParams, { replace: true });
  };

  const developmentsQuery = useInfiniteQuery({
    queryKey: ["admin-developments", search, sortColumn, sortDirection],
    queryFn: ({ pageParam = 0 }) => listDevelopments(search || undefined, pageParam, 20, sortColumn, sortDirection),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const result = lastPage.result;
      if (!result || result.page >= result.totalPages - 1) {
        return undefined;
      }
      return result.page + 1;
    },
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && developmentsQuery.hasNextPage && !developmentsQuery.isFetchingNextPage) {
          developmentsQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [developmentsQuery.hasNextPage, developmentsQuery.isFetchingNextPage, developmentsQuery.fetchNextPage]);

  const developmentDetailsQuery = useQuery({
    queryKey: ["admin-development", selectedDevelopmentId],
    queryFn: () => getDevelopmentDetails(selectedDevelopmentId!),
    enabled: selectedDevelopmentId !== null,
  });

  const updateOwnerMutation = useMutation({
    mutationFn: ({ developmentId, ownerAccountId }: { developmentId: number; ownerAccountId: number }) =>
      updateDevelopmentOwner(developmentId, { ownerAccountId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-development", selectedDevelopmentId] });
      queryClient.invalidateQueries({ queryKey: ["admin-developments"] });
      setShowChangeOwnerModal(false);
    },
  });

  const updateNameMutation = useMutation({
    mutationFn: ({ developmentId, name }: { developmentId: number; name: string }) =>
      updateDevelopmentName(developmentId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-development", selectedDevelopmentId] });
      queryClient.invalidateQueries({ queryKey: ["admin-developments"] });
      setShowEditNameModal(false);
    },
  });

  const loadedDevelopments = developmentsQuery.data?.pages.flatMap((page) => page.result?.items ?? []) ?? [];
  const developmentDetails = developmentDetailsQuery.data?.result;

  // If selected development is not in the loaded list, prepend it from the details query
  const selectedDevelopmentInList = selectedDevelopmentId
    ? loadedDevelopments.some((d) => d.id === selectedDevelopmentId)
    : true;
  const developments =
    !selectedDevelopmentInList && developmentDetails
      ? [
          { id: developmentDetails.id, name: developmentDetails.name },
          ...loadedDevelopments.filter((d) => d.id !== developmentDetails.id),
        ]
      : loadedDevelopments;

  // Scroll to selected row when data is loaded and row is available
  useEffect(() => {
    if (selectedDevelopmentId && (developments.length > 0 || developmentDetails)) {
      requestAnimationFrame(() => {
        if (selectedRowRef.current) {
          selectedRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    }
  }, [selectedDevelopmentId, developments.length, developmentDetails]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const SortIndicator = ({ column }: { column: SortColumn }) => (
    <span className="ml-1">{sortColumn === column ? (sortDirection === "asc" ? "▲" : "▼") : ""}</span>
  );

  const renderExpandedRow = (developmentId: number) => {
    if (selectedDevelopmentId !== developmentId) {
      return null;
    }

    return (
      <tr key={`details-${developmentId}`}>
        <td colSpan={2} className="p-0 border-b">
          <div className="bg-gray-50 border-t">
            {developmentDetailsQuery.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="w-8 h-8" />
              </div>
            ) : developmentDetails ? (
              <div>
                {/* Header */}
                <div className="flex items-start justify-between p-4 pb-3 border-b border-gray-200">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{developmentDetails.name}</h2>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <span>ID: {developmentDetails.id}</span>
                      <span>-</span>
                      <span>{developmentDetails.buildingsCount} building(s)</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowEditNameModal(true)}
                      className="btn-custom px-3 py-1.5 text-sm text-dobu-orange hover:bg-orange-50 rounded transition-colors"
                    >
                      Edit Name
                    </button>
                    <button
                      onClick={() => onSelectedDevelopmentIdChange(null)}
                      className="btn-custom p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                      title="Close"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Collapsible Sections */}
                <div className="px-4">
                  {/* Owner Account Section */}
                  <CollapsibleSection
                    title="Owner Account"
                    action={
                      <button
                        onClick={() => setShowChangeOwnerModal(true)}
                        className="btn-custom px-2 py-1 text-xs font-medium text-dobu-orange hover:bg-orange-50 rounded transition-colors"
                      >
                        Change
                      </button>
                    }
                  >
                    {developmentDetails.ownerAccount ? (
                      <div
                        className="p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors max-w-md"
                        onClick={() => onSelectAccount(developmentDetails.ownerAccount!.id)}
                      >
                        <div className="font-medium text-sm">{developmentDetails.ownerAccount.name}</div>
                        <div className="text-xs text-gray-500">
                          {developmentDetails.ownerAccount.type} - ID: {developmentDetails.ownerAccount.id}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No owner account</p>
                    )}
                  </CollapsibleSection>

                  {/* Buildings Section */}
                  <CollapsibleSection title="Buildings" count={developmentDetails.buildings.length}>
                    {developmentDetails.buildings.length === 0 ? (
                      <p className="text-gray-500 text-sm">No buildings in this development</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                        {developmentDetails.buildings.map((building) => (
                          <div
                            key={building.id}
                            className="p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-gray-300 transition-colors"
                            onClick={() => onSelectBuilding(building.id)}
                          >
                            <div className="font-medium text-sm truncate">
                              {building.street}, {building.city}
                            </div>
                            <div className="text-xs text-gray-500">
                              {building.postcode} - ID: {building.id}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CollapsibleSection>

                  {/* Logo Section */}
                  <CollapsibleSection title="Logo">
                    <LogoEditor
                      developmentId={developmentDetails.id}
                      logoUrl={developmentDetails.logoUrl}
                      maxWidth={developmentDetails.logoMaxWidth}
                      maxHeight={developmentDetails.logoMaxHeight}
                    />
                  </CollapsibleSection>
                </div>
              </div>
            ) : (
              <div className="text-red-500">Failed to load development details</div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className="flex gap-4 mb-4">
        <input
          type="text"
          placeholder="Search developments..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-dobu-orange"
        />
      </div>

      {developmentsQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner className="w-8 h-8" />
        </div>
      ) : developmentsQuery.isError ? (
        <div className="text-red-500 py-4">Failed to load developments</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("id")}
                  >
                    ID
                    <SortIndicator column="id" />
                  </th>
                  <th
                    className="p-3 text-left font-medium cursor-pointer hover:bg-gray-200"
                    onClick={() => handleSort("name")}
                  >
                    Name
                    <SortIndicator column="name" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {developments.map((development) => (
                  <React.Fragment key={development.id}>
                    <tr
                      ref={selectedDevelopmentId === development.id ? selectedRowRef : null}
                      className={`border-b cursor-pointer hover:bg-gray-50 ${
                        selectedDevelopmentId === development.id ? "bg-blue-50" : ""
                      }`}
                      onClick={() =>
                        onSelectedDevelopmentIdChange(selectedDevelopmentId === development.id ? null : development.id)
                      }
                    >
                      <td className="p-3">{development.id}</td>
                      <td className="p-3">{development.name}</td>
                    </tr>
                    {renderExpandedRow(development.id)}
                  </React.Fragment>
                ))}
                {developments.length === 0 && (
                  <tr>
                    <td colSpan={2} className="p-4 text-center text-gray-500">
                      No developments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div ref={loadMoreRef} className="flex justify-center py-4">
            {developmentsQuery.isFetchingNextPage && <Spinner className="w-6 h-6" />}
          </div>
        </>
      )}

      {showChangeOwnerModal && selectedDevelopmentId && (
        <ChangeOwnerModal
          currentOwnerAccountId={developmentDetails?.ownerAccount?.id ?? null}
          onClose={() => setShowChangeOwnerModal(false)}
          onSelect={(accountId) =>
            updateOwnerMutation.mutate({ developmentId: selectedDevelopmentId, ownerAccountId: accountId })
          }
          isLoading={updateOwnerMutation.isPending}
        />
      )}

      {showEditNameModal && selectedDevelopmentId && developmentDetails && (
        <EditNameModal
          currentName={developmentDetails.name}
          onClose={() => setShowEditNameModal(false)}
          onSave={(name) => updateNameMutation.mutate({ developmentId: selectedDevelopmentId, name })}
          isLoading={updateNameMutation.isPending}
        />
      )}
    </div>
  );
}

function ChangeOwnerModal({
  currentOwnerAccountId,
  onClose,
  onSelect,
  isLoading,
}: {
  currentOwnerAccountId: number | null;
  onClose: () => void;
  onSelect: (accountId: number) => void;
  isLoading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<AdminAccount | null>(null);

  const accountsQuery = useQuery({
    queryKey: ["admin-accounts-for-owner", search],
    queryFn: () => listAccounts(search || undefined, 0, 10),
    enabled: search.length >= 2,
  });

  const availableAccounts = (accountsQuery.data?.result?.items ?? []).filter((a) => a.id !== currentOwnerAccountId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Change Owner Account</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Search Account</label>
            <input
              type="text"
              placeholder="Type at least 2 characters..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedAccount(null);
              }}
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
            />
          </div>

          {accountsQuery.isLoading && (
            <div className="flex justify-center py-2">
              <Spinner className="w-6 h-6" />
            </div>
          )}

          {availableAccounts.length > 0 && (
            <ul className="max-h-48 overflow-y-auto border rounded">
              {availableAccounts.map((account) => (
                <li
                  key={account.id}
                  className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedAccount?.id === account.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedAccount(account)}
                >
                  <div className="font-medium">{account.name}</div>
                  <div className="text-sm text-gray-500">
                    {account.type} - ID: {account.id}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {search.length >= 2 && !accountsQuery.isLoading && availableAccounts.length === 0 && (
            <div className="text-gray-500 text-center py-2">No accounts found</div>
          )}

          <div className="flex justify-between gap-2 pt-4">
            <button
              onClick={() => onSelect(DEFAULT_OWNER_ACCOUNT_ID)}
              disabled={isLoading || currentOwnerAccountId === DEFAULT_OWNER_ACCOUNT_ID}
              className="btn-custom px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
            >
              Reset to Default
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="btn-custom px-4 py-2 border rounded hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={() => selectedAccount && onSelect(selectedAccount.id)}
                disabled={isLoading || !selectedAccount}
                className="btn-custom px-4 py-2 bg-dobu-orange text-white rounded hover:bg-dobu-orange/90 disabled:opacity-50"
              >
                {isLoading ? "Saving..." : "Select"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditNameModal({
  currentName,
  onClose,
  onSave,
  isLoading,
}: {
  currentName: string;
  onClose: () => void;
  onSave: (name: string) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(currentName);

  const hasChanges = name !== currentName;
  const isValid = name.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-96 max-w-full">
        <h2 className="text-xl font-bold mb-4">Edit Development Name</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-dobu-orange"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="btn-custom px-4 py-2 border rounded hover:bg-gray-100">
              Cancel
            </button>
            <button
              onClick={() => onSave(name.trim())}
              disabled={isLoading || !hasChanges || !isValid}
              className="btn-custom px-4 py-2 bg-dobu-orange text-white rounded hover:bg-dobu-orange/90 disabled:opacity-50"
            >
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
